import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  GitCompare,
  ArrowRight,
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  Loader2,
  AlertCircle,
  History,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Confidence level style helper
const getConfidenceBadgeStyle = (level) => {
  if (level === 'high') return 'bg-emerald-500';
  if (level === 'medium') return 'bg-amber-500';
  return 'bg-red-500';
};

// Run selector dropdown
const RunSelector = ({ runs, selectedId, onChange, label }) => (
  <div className="flex-1">
    <div className="text-xs text-slate-400 mb-1">{label}</div>
    <select
      value={selectedId || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-md bg-slate-800 border border-slate-600 text-white text-sm px-3"
    >
      <option value="">Select a run...</option>
      {runs.map(run => (
        <option key={run.test_run_id} value={run.test_run_id}>
          {run.test_run_id} - {run.recommendation.toUpperCase()} ({new Date(run.timestamp).toLocaleTimeString()})
        </option>
      ))}
    </select>
  </div>
);

// Diff value display with change indicator
const DiffValue = ({ value, changed, isRight }) => {
  if (value === true) return <Badge className="bg-emerald-500">Yes</Badge>;
  if (value === false) return <Badge className="bg-slate-600">No</Badge>;
  if (Array.isArray(value)) return <span className="text-white text-sm">{value.join(', ')}</span>;
  
  const getTextClass = () => {
    if (!changed) return 'text-white';
    return isRight ? 'text-cyan-400 font-medium' : 'text-slate-400';
  };
  return <span className={`text-sm ${getTextClass()}`}>{String(value)}</span>;
};

// Change indicator icon
const ChangeIndicator = ({ changeType }) => {
  const indicators = {
    improved: <TrendingUp className="w-4 h-4 text-emerald-400" />,
    degraded: <TrendingDown className="w-4 h-4 text-red-400" />
  };
  return indicators[changeType] || <Minus className="w-4 h-4 text-slate-500" />;
};

// Diff row component
const DiffRow = ({ diff }) => (
  <div className={`grid grid-cols-[1fr_auto_1fr] gap-4 py-2 px-3 rounded ${diff.changed ? 'bg-cyan-500/10 border border-cyan-500/20' : ''}`}>
    <DiffValue value={diff.value_a} changed={diff.changed} isRight={false} />
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500 w-24 text-center">{diff.label}</span>
      {diff.changed && <ChangeIndicator changeType={diff.change_type} />}
    </div>
    <DiffValue value={diff.value_b} changed={diff.changed} isRight={true} />
  </div>
);

// Section header for diff groups
const DiffSection = ({ title, diffs, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const changedCount = diffs.filter(d => d.changed).length;
  
  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2 bg-slate-800/50 hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{title}</span>
          {changedCount > 0 && (
            <Badge className="bg-cyan-600 text-xs">{changedCount} changed</Badge>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {isOpen && (
        <div className="p-2 space-y-1">
          {diffs.map((diff, i) => (
            <DiffRow key={diff.field} diff={diff} />
          ))}
        </div>
      )}
    </div>
  );
};

const CompareRuns = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [runIdA, setRunIdA] = useState(null);
  const [runIdB, setRunIdB] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState(null);

  // Load run history
  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/passenger/lab/history`);
        const data = await res.json();
        setHistory(data.runs || []);
      } catch (err) {
        setError('Failed to load history');
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - load once on mount

  const compareRuns = useCallback(async () => {
    if (!runIdA || !runIdB) return;
    
    setComparing(true);
    setError(null);
    setComparison(null);
    
    try {
      const res = await fetch(`${API_URL}/api/passenger/lab/compare?run_id_a=${runIdA}&run_id_b=${runIdB}`, {
        method: 'POST'
      });
      
      if (!res.ok) {
        throw new Error('Comparison failed');
      }
      
      const data = await res.json();
      setComparison(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setComparing(false);
    }
  }, [runIdA, runIdB]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (history.length < 2) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6 text-center">
          <History className="w-10 h-10 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400">Run at least 2 scenarios to compare results.</p>
          <p className="text-slate-500 text-sm mt-2">Current runs: {history.length}/2 required</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="compare-runs" className="space-y-4">
      {/* Run Selection */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <GitCompare className="w-4 h-4 text-cyan-400" />
            Compare Test Runs
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex gap-4 items-end">
            <RunSelector 
              runs={history} 
              selectedId={runIdA} 
              onChange={setRunIdA}
              label="Run A (Baseline)"
            />
            <ArrowRight className="w-5 h-5 text-slate-500 mb-2" />
            <RunSelector 
              runs={history} 
              selectedId={runIdB} 
              onChange={setRunIdB}
              label="Run B (Comparison)"
            />
            <Button
              onClick={compareRuns}
              disabled={!runIdA || !runIdB || runIdA === runIdB || comparing}
              className="bg-cyan-600 hover:bg-cyan-700 mb-0"
            >
              {comparing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Compare'
              )}
            </Button>
          </div>
          {runIdA && runIdB && runIdA === runIdB && (
            <p className="text-amber-400 text-xs mt-2">Select two different runs to compare</p>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Comparison Results */}
      {comparison && (
        <div className="space-y-4">
          {/* Summary Banner */}
          <Card className={`border-2 ${
            comparison.recommendation_changed 
              ? 'bg-amber-500/10 border-amber-500/50' 
              : 'bg-emerald-500/10 border-emerald-500/50'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${comparison.recommendation_changed ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`}>
                    {comparison.recommendation_changed ? (
                      <AlertCircle className="w-5 h-5 text-amber-400" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm text-slate-400">Comparison Summary</div>
                    <div className="text-lg font-semibold text-white">
                      {comparison.total_changes} parameter changes detected
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  {comparison.recommendation_changed && (
                    <Badge className="bg-amber-500">Recommendation Changed</Badge>
                  )}
                  {comparison.risk_improved && (
                    <Badge className="bg-emerald-500">Risk Improved</Badge>
                  )}
                  {comparison.confidence_changed && (
                    <Badge className="bg-cyan-500">Confidence Changed</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Run Headers */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-slate-400">Run A (Baseline)</Badge>
                  <span className="text-xs text-slate-500">
                    {new Date(comparison.run_a.timestamp).toLocaleString()}
                  </span>
                </div>
                <div className="text-lg font-bold text-white">{comparison.run_a.recommendation.toUpperCase()}</div>
                <div className="flex gap-2 mt-2">
                  <Badge className={getConfidenceBadgeStyle(comparison.run_a.confidence_level)}>
                    {comparison.run_a.confidence_level}
                  </Badge>
                  {comparison.run_a.katlas_signed && (
                    <Badge className="bg-cyan-600">Signed</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-cyan-400 border-cyan-500/50">Run B (Comparison)</Badge>
                  <span className="text-xs text-slate-500">
                    {new Date(comparison.run_b.timestamp).toLocaleString()}
                  </span>
                </div>
                <div className="text-lg font-bold text-white">{comparison.run_b.recommendation.toUpperCase()}</div>
                <div className="flex gap-2 mt-2">
                  <Badge className={getConfidenceBadgeStyle(comparison.run_b.confidence_level)}>
                    {comparison.run_b.confidence_level}
                  </Badge>
                  {comparison.run_b.katlas_signed && (
                    <Badge className="bg-cyan-600">Signed</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Diff Sections */}
          <DiffSection 
            title="Outcome Differences" 
            diffs={comparison.outcome_diffs}
            defaultOpen={true}
          />
          <DiffSection 
            title="Parameter Differences" 
            diffs={comparison.parameter_diffs}
            defaultOpen={true}
          />
          <DiffSection 
            title="Advisory Processing" 
            diffs={comparison.advisory_diffs}
            defaultOpen={false}
          />
        </div>
      )}
    </div>
  );
};

export default CompareRuns;
